import { LoadingThumbnail } from '@app/core/thumbnails/components/LoadingThumbnail';
import { SegmentThumbnail } from '@app/core/thumbnails/components/SegmentThumbnail';
import { isPreviewThumbLoadingEnabled } from '@app/core/thumbnails/consts';
import { generateFallbackThumbnailDisplayInfo } from '@app/core/thumbnails/utils';
import { Station } from '@app/core/types/stations';
import {
  THUMB_PREVIEW_WIDTH,
  THUMB_PREVIEW_WIDTH_COMPACT,
  THUMBNAIL_HEIGHT_COMPACT,
  THUMBNAIL_HEIGHT_DEFAULT,
  THUMBNAIL_LIVE_OFFSET_MS,
} from '@app/modules/logger/screens/status/constants';
import { Segment } from '@mi-front/core';
import { Box, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useStatusViewContext } from '../../../../context/StatusViewContext';

interface Props {
  readonly station: Station;
  readonly segments: readonly Segment[];
}

/**
 * Displays the thumbnail for the latest (first) segment start.
 * Used when the user is not hovering over the waveform.
 */
export function LatestStatusThumbnail(p: Props) {
  const { station, segments } = p;
  const { compact } = useStatusViewContext();
  const width = compact ? THUMB_PREVIEW_WIDTH_COMPACT : THUMB_PREVIEW_WIDTH;
  const targetHeight = compact ? THUMBNAIL_HEIGHT_COMPACT : THUMBNAIL_HEIGHT_DEFAULT;

  const latestSegment = segments.slice(-1)[0];

  const tooltipTitle = useMemo(
    () =>
      `Última miniatura (${DateTime.fromMillis(latestSegment.endTs - THUMBNAIL_LIVE_OFFSET_MS)
        .setZone(station.timezone)
        .toFormat('HH:mm:ss')})`,
    [latestSegment, station.timezone],
  );

  return (
    <Box
      sx={{
        width,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isPreviewThumbLoadingEnabled() ? (
        <LoadingThumbnail
          metadata={generateFallbackThumbnailDisplayInfo()}
          targetHeight={targetHeight}
          logoUrl={station.logoUrl}
          stationName={station.name}
        />
      ) : (
        <Tooltip title={tooltipTitle}>
          <Box>
            <SegmentThumbnail
              stationId={station.id}
              segmentId={latestSegment.id}
              offsetSeconds={Math.floor((latestSegment.duration - THUMBNAIL_LIVE_OFFSET_MS) / 1000)}
              targetHeight={targetHeight}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
